import React, { useMemo } from 'react';
import { NumberInput, Dropdown } from '@carbon/react';

// 文字列 → {y,m,d}
function parsePartial(value) {
  if (!value) return { y: '', m: '', d: '' };
  const s = String(value).trim().replace(/-/g, '/');

  // ✅ 追加: YYYYMM → {y, m}
  if (/^\d{6}$/.test(s)) {
    const y = s.slice(0, 4);
    const m = s.slice(4, 6);
    return { y, m, d: '' };
  }

  // ✅ ISO形式（"2025-10-03T..."）
  const iso = new Date(value);
  if (!Number.isNaN(iso.getTime()) && /^\d{4}-\d{2}-\d{2}T?/.test(String(value))) {
    return {
      y: String(iso.getFullYear()),
      m: String(iso.getMonth() + 1),
      d: String(iso.getDate()),
    };
  }

  // ✅ YYYY / YYYY/MM / YYYY/MM/DD
  const parts = s.split('/');
  if (parts.length === 1 && /^\d{4}$/.test(parts[0])) return { y: parts[0], m: '', d: '' };
  if (parts.length === 2) return { y: parts[0] || '', m: parts[1] || '', d: '' };
  if (parts.length >= 3) return { y: parts[0] || '', m: parts[1] || '', d: parts[2] || '' };

  return { y: '', m: '', d: '' };
}

// {y,m,d} → 文字列
function serializePartial({ y, m, d }) {
  if (!y) return '';
  if (!m) return String(y);
  if (!d) return `${y}/${String(m)}`;
  return `${y}/${String(m).padStart(2,'0')}/${String(d).padStart(2,'0')}`;
}
function daysInMonth(y, m) {
  const yy = Number(y), mm = Number(m);
  if (!yy || !mm) return 31;
  return new Date(yy, mm, 0).getDate();
}

export default function PartialDateInput({ id, label, value, onChange, showHint = true }) {
  const { y, m, d } = useMemo(() => parsePartial(value), [value]);

  const yearNum = y ? Number(y) : '';
  const monthNum = m ? Number(m) : '';
  const maxDay = daysInMonth(yearNum, monthNum);
  const dayNum = d ? Math.min(Number(d), maxDay) : '';
  const nowYear = new Date().getFullYear();
  const minYear = 1900;
  const maxYear = nowYear + 1;

  const years = useMemo(
    () => Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
      const yy = minYear + i;
      return { id: yy, text: String(yy) };
    }),
    [minYear, maxYear]
  );
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => ({ id: i+1, text: String(i+1) })), []);
  const days = useMemo(() => Array.from({ length: maxDay }, (_, i) => ({ id: i+1, text: String(i+1) })), [maxDay]);

  const DEFAULT_YEAR = '2020';

  const handleYear = ({ selectedItem }) => {
    // Dropdown を触った瞬間、未設定なら 2020 を初期値にする
    const baseYear = y || DEFAULT_YEAR;

    const yv = selectedItem?.id
      ? String(selectedItem.id)
      : baseYear;

    const dd = dayNum
      ? String(Math.min(Number(dayNum), daysInMonth(yv, m)))
      : '';

    onChange(id, serializePartial({ y: yv, m, d: dd }));
  };

  // const handleYear = (_, { value: next }) => {
  //   const yv = next === '' ? '' : String(next).replace(/\D/g, '').slice(0,4);
  //   onChange(id, serializePartial({ y: yv, m, d }));
  // };
  const handleMonth = ({ selectedItem }) => {
    const mv = selectedItem?.id ? String(selectedItem.id) : '';
    const dd = dayNum ? String(Math.min(Number(dayNum), daysInMonth(y, mv))) : '';
    onChange(id, serializePartial({ y, m: mv, d: dd }));
  };
  const handleDay = ({ selectedItem }) => {
    const dv = selectedItem?.id ? String(selectedItem.id) : '';
    onChange(id, serializePartial({ y, m, d: dv }));
  };

  return (
    <div>
      <div style={{ marginBottom: 6, fontSize: 12, color: '#525252' }}>{label}</div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 130 }}>
          <Dropdown
            id={`${id}-year`}
            label="年"
            items={years}
            itemToString={(it) => it?.text ?? ''}
            selectedItem={yearNum ? years.find((it) => it.id === yearNum) : null}
            onChange={handleYear}
          />
        </div>
        <div style={{ minWidth: 110 }}>
          <Dropdown
            id={`${id}-month`}
            label={monthNum ? String(monthNum) : '月'}
            items={months}
            itemToString={(it) => it?.text ?? ''}
            selectedItem={monthNum ? months[monthNum-1] : null}
            onChange={handleMonth}
            // titleText を付けないことで余計なラベル余白を発生させない
          />
        </div>
        <div style={{ minWidth: 110 }}>
          <Dropdown
            id={`${id}-day`}
            label={dayNum ? String(dayNum) : '日'}
            items={days}
            itemToString={(it) => it?.text ?? ''}
            selectedItem={dayNum ? days[dayNum-1] : null}
            onChange={handleDay}
          />
        </div>
      </div>

      {showHint && (
        // Carbon のフォーム間隔に影響されないよう、このコンテナ内で最小余白にする
        <div style={{ marginTop: '0.25rem', lineHeight: 1.3, fontSize: 12, color: '#6f6f6f' }}>
          年 / 年月 / 年月日 のいずれでもOK（未入力の欄はスキップ）
        </div>
      )}
    </div>
  );
}
